import React, { useMemo, useState } from 'react';

import { useDebounce } from '@/lib/hooks/useDebounce';
import { useStableJson } from '@/lib/hooks/useStableJson';

import ContentRenderer from '../../core/ContentRenderer';
import { ContentPayload, RendererConfig, TextFormattingOptions } from '../../types';
import { getErrorPosition } from '../../utils';
import styles from './Previewer.module.scss';

export interface PreviewerProps {
  value: string;
  debounceMs?: number;
  config?: Partial<RendererConfig>;
  textOptions?: Partial<TextFormattingOptions>;
  className?: string;
  showErrorDetails?: boolean;
  mediaType?: 'video' | undefined;
  mediaUrl?: string | undefined;
}

export const Previewer: React.FC<PreviewerProps> = ({
  value,
  debounceMs = 300,
  config = {},
  textOptions = {},
  className = '',
  showErrorDetails = true,
  mediaType,
  mediaUrl,
}) => {
  const debouncedValue = useDebounce(value, debounceMs);
  const { data, error } = useStableJson<ContentPayload>(debouncedValue);

  const [isExpanded, setIsExpanded] = useState(false);

  const errorDetails = useMemo(() => {
    if (!error || !showErrorDetails) return null;

    try {
      JSON.parse(debouncedValue);
      return {
        message: error,
        details: null,
        position: null,
      };
    } catch (parseError) {
      return {
        message: error,
        details: parseError instanceof Error ? parseError.message : 'Unknown parsing error',
        position: getErrorPosition(debouncedValue, parseError),
      };
    }
  }, [error, debouncedValue, showErrorDetails]);

  if (error) {
    return (
      <div className={`${styles.errorContainer} ${className}`}>
        <div className={styles.errorHeader}>
          <div className={styles.errorIcon}>⚠️</div>
          <div>
            <div className={styles.errorTitle}>JSON Error</div>
            <div className={styles.errorMessage}>{error}</div>
          </div>
        </div>

        {errorDetails && (
          <div className={styles.errorDetails}>
            <button onClick={() => setIsExpanded(!isExpanded)} className={styles.expandButton}>
              {isExpanded ? 'Hide' : 'Show'} Details
            </button>

            {isExpanded && (
              <div className={styles.errorExpanded}>
                {errorDetails.details && (
                  <div className={styles.errorDetailItem}>
                    <strong>Details:</strong> {errorDetails.details}
                  </div>
                )}
                {errorDetails.position && (
                  <div className={styles.errorDetailItem}>
                    <strong>Position:</strong> {errorDetails.position}
                  </div>
                )}
                <div className={styles.errorSuggestion}>
                  <strong>Suggestion:</strong> Check your JSON syntax and try again.
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  if (!data && !(mediaType === 'video' && mediaUrl)) {
    return (
      <div className={`${styles.emptyContainer} ${className}`}>
        <div className={styles.emptyIcon}>📝</div>
        <div className={styles.emptyTitle}>No content to preview</div>
        <div className={styles.emptySubtitle}>Enter valid JSON above to see the preview</div>
      </div>
    );
  }

  if (mediaType === 'video' && mediaUrl) {
    return (
      <div className={`${styles.previewer} ${className}`}>
        <div className={styles.contentWrapper}>
          <div className="w-full aspect-video bg-black/5 rounded-md overflow-hidden">
            <video controls className="w-full h-full" src={mediaUrl} autoPlay />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.previewer} ${className}`}>
      <ContentRenderer
        content={data || []}
        config={config}
        textOptions={textOptions}
        className={styles.contentWrapper}
      />
    </div>
  );
};

export default Previewer;
